// ── AI-suggested configuration — review/apply banner ─────────────────────────
//
// Shown at the top of a stage's config panel(s) whenever "AI Configure from
// Scope" has produced a suggestion for that stage. Mirrors the review-before-
// apply pattern of PocAiAssist/BlueprintTab: nothing is ever seeded into the
// panel below until the rep explicitly clicks Apply.
import { Sparkles, Check, X } from 'lucide-react'
import type { AiConfidence } from '../aiConfigure'

const CONFIDENCE_STYLE: Record<AiConfidence, { bg: string; color: string; label: string }> = {
  high: { bg: 'rgba(52,211,153,.15)', color: '#15803d', label: 'High confidence' },
  medium: { bg: 'rgba(251,191,36,.15)', color: '#b45309', label: 'Medium confidence' },
  low: { bg: 'rgba(248,113,113,.15)', color: '#b91c1c', label: 'Low confidence' },
}

interface AiSuggestionBannerProps {
  rationale: string
  confidence: AiConfidence
  applied: boolean
  onApply: () => void
  onDismiss: () => void
}

export function AiSuggestionBanner({ rationale, confidence, applied, onApply, onDismiss }: AiSuggestionBannerProps) {
  const cs = CONFIDENCE_STYLE[confidence]
  return (
    <div className="rounded-[14px] p-4 space-y-2.5" style={{ border: '1px solid rgba(99,102,241,.3)', background: 'rgba(99,102,241,.04)' }}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-[9px] flex items-center justify-center shrink-0" style={{ background: 'rgba(99,102,241,.15)' }}>
          <Sparkles size={15} className="text-[#4f46e5]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[12px] font-semibold text-[#1e293b]">AI-suggested configuration</p>
            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0" style={{ background: cs.bg, color: cs.color }}>
              {cs.label}
            </span>
          </div>
          <p className="text-[11px] text-[#475569] mt-1 leading-snug">{rationale}</p>
        </div>
      </div>

      {applied ? (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-[10px] text-emerald-700 flex items-center gap-1.5">
            <Check size={11} className="shrink-0" /> Applied below — edit any field to fine-tune, or re-apply to reset it.
          </p>
          <button onClick={onApply} className="text-[10px] font-medium text-[#4f46e5] hover:underline shrink-0">Re-apply</button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={onApply}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[11px] font-semibold text-white transition-all"
            style={{ background: '#4f46e5', boxShadow: '0 3px 12px rgba(79,70,229,.35)' }}
          >
            <Check size={12} /> Apply suggested config
          </button>
          <button
            onClick={onDismiss}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[11px] font-medium transition-colors"
            style={{ background: '#f8fafc', border: '1px solid #e5e7eb', color: '#475569' }}
          >
            <X size={12} /> Dismiss
          </button>
        </div>
      )}
    </div>
  )
}
